/**
 * COMPOSANT: SelectAllBanner
 * Bandeau style Gmail pour sélectionner tous les résultats filtrés (pas juste la page visible)
 *
 * Affiche: "25 contacts sélectionnés sur cette page. Sélectionner les 690 contacts correspondant..."
 *
 * Feature 4.4 - Sprint 4
 */

interface SelectAllBannerProps {
  /** Nombre d'éléments actuellement sélectionnés */
  selectedCount: number;
  /** Nombre d'éléments sur la page courante */
  pageItemCount: number;
  /**
   * Vrai si TOUS les éléments de la page courante sont sélectionnés (test
   * d'appartenance via isAllPageSelected, pas un simple comptage — ticket #195 :
   * selectedCount >= pageItemCount était trompé par une sélection issue d'une
   * autre page/d'un autre périmètre de filtres)
   */
  allPageSelected: boolean;
  /** Nombre total d'éléments correspondant aux filtres */
  totalFilteredCount: number;
  /** Callback pour sélectionner tous les résultats filtrés */
  onSelectAll: () => void;
  /** Callback pour annuler la sélection globale */
  onClearSelection: () => void;
  /** Nom de l'entité au pluriel (ex: "contacts", "équipages") */
  entityNamePlural: string;
  /** Indique si tous les résultats filtrés sont déjà sélectionnés */
  isAllSelected: boolean;
  /** Indique si le chargement de tous les IDs est en cours */
  isLoading?: boolean;
}

export function SelectAllBanner({
  selectedCount,
  pageItemCount,
  allPageSelected,
  totalFilteredCount,
  onSelectAll,
  onClearSelection,
  entityNamePlural,
  isAllSelected,
  isLoading = false,
}: SelectAllBannerProps) {
  // Ne rien afficher si aucune sélection
  if (selectedCount === 0) return null;

  // Ne rien afficher s'il n'y a pas plus d'éléments que sur la page
  if (totalFilteredCount <= pageItemCount) return null;

  // Si tous les résultats sont déjà sélectionnés, afficher un message différent.
  // Le double test sur selectedCount couvre le décochage d'une ligne après une
  // sélection globale : le flag isAllSelected du parent reste levé mais la
  // sélection n'est plus complète — sans ce garde le bandeau mentirait.
  if (isAllSelected && selectedCount >= totalFilteredCount) {
    return (
      <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 text-sm text-blue-700">
        <span>
          Les <strong>{totalFilteredCount}</strong> {entityNamePlural} correspondant à cette
          recherche sont sélectionnés.{' '}
        </span>
        <button
          onClick={onClearSelection}
          className="font-medium text-blue-900 hover:underline"
          disabled={isLoading}
        >
          Annuler la sélection
        </button>
      </div>
    );
  }

  // Si la page n'est pas entièrement sélectionnée, ne pas afficher le bandeau
  if (!allPageSelected) return null;

  return (
    <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 text-sm text-blue-700">
      <span>
        {/* pageItemCount et non selectedCount : la sélection peut aussi porter
            sur d'autres pages, seul le compte de la page courante est vrai ici */}
        <strong>{pageItemCount}</strong> {entityNamePlural} sélectionné
        {pageItemCount > 1 ? 's' : ''} sur cette page.{' '}
      </span>
      <button
        onClick={onSelectAll}
        className="font-medium text-blue-900 hover:underline disabled:opacity-50 disabled:cursor-wait"
        disabled={isLoading}
      >
        {isLoading
          ? 'Chargement...'
          : `Sélectionner les ${totalFilteredCount} ${entityNamePlural} correspondant à cette recherche`}
      </button>
    </div>
  );
}
